import type { CobroTarjetaVista, ReembolsoTarjetaVista, TerminalVista, TurnoCajaVista, VentaVista } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { api, ErrorApi } from '@/api/cliente';
import { avisarError } from '@/lib/avisos';
import { useConfig } from '../agenda/datos';

export const useTurnoActual = () =>
  useQuery({
    queryKey: ['caja', 'turno'],
    queryFn: () => api<{ turno: TurnoCajaVista | null; fondoSugerido: { MXN: number; USD: number } }>('/caja/turno-actual'),
  });

export const usePorCobrar = () => useQuery({ queryKey: ['ventas', 'por-cobrar'], queryFn: () => api<VentaVista[]>('/ventas/por-cobrar') });
/** Carritos de servicios que siguen en curso (se cobran al finalizar). */
export const useEnServicio = () => useQuery({ queryKey: ['ventas', 'en-servicio'], queryFn: () => api<VentaVista[]>('/ventas/en-servicio') });
export const useSuspendidas = () => useQuery({ queryKey: ['ventas', 'suspendidas'], queryFn: () => api<VentaVista[]>('/ventas/suspendidas') });

export function useDenominaciones() {
  return {
    MXN: useConfig<number[]>('caja.denominacionesMXN', [100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50]),
    USD: useConfig<number[]>('caja.denominacionesUSD', [10000, 5000, 2000, 1000, 500, 100]),
  };
}

/** Invalida todo lo que depende de la caja y las ventas. */
export function useRefrescarCaja() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['caja'] });
    void qc.invalidateQueries({ queryKey: ['ventas'] });
  };
}

/**
 * Terminales Point activas de la sucursal. Módulo apagado o sin cuenta (409) o sin permiso
 * equivalen a «sin terminales»: la caja simplemente no ofrece Tarjeta, sin avisos de error.
 */
export const useTerminalesActivas = () =>
  useQuery({
    queryKey: ['terminales-activas'],
    queryFn: async () => {
      try {
        return await api<TerminalVista[]>('/mercadopago/terminales/activas');
      } catch (e) {
        if (e instanceof ErrorApi && e.status < 500) return [];
        throw e;
      }
    },
    staleTime: 30_000,
  });

/**
 * Cobro con tarjeta pendiente (o el último) de la venta; se consulta cada 3 s mientras siga
 * pendiente o mientras la caja espera un resultado (`esperando`), aunque llegue una respuesta vieja.
 */
export const useCobroTarjeta = (ventaId: string | undefined, esperando = false) =>
  useQuery({
    queryKey: ['cobro-tarjeta', ventaId],
    queryFn: () => api<CobroTarjetaVista | null>(`/ventas/${ventaId}/cobro-tarjeta`),
    enabled: Boolean(ventaId),
    staleTime: 0,
    refetchInterval: (q) => (esperando || q.state.data?.estado === 'pendiente' ? 3000 : false),
  });

/** Estado de la red del navegador (la tarjeta exige conexión). */
export function useEnLinea(): boolean {
  const [enLinea, setEnLinea] = useState(() => navigator.onLine);
  useEffect(() => {
    const cambio = () => setEnLinea(navigator.onLine);
    window.addEventListener('online', cambio);
    window.addEventListener('offline', cambio);
    return () => {
      window.removeEventListener('online', cambio);
      window.removeEventListener('offline', cambio);
    };
  }, []);
  return enLinea;
}

/** Mensajes en español por código de error de la caja; los demás usan el mensaje del servidor. */
const MENSAJES_ERROR_CAJA: Readonly<Record<string, string>> = {
  COBRO_TARJETA_PENDIENTE: 'Hay un cobro con tarjeta en curso. Espera a que termine o cancélalo antes de cerrar la caja.',
  COBRO_APROBADO_SIN_REGISTRAR: 'Mercado Pago ya aprobó este cobro y se está registrando. No lo canceles ni vuelvas a cobrar; espera unos segundos.',
  REEMBOLSO_FALLIDO: 'El reembolso ya no cabe en lo cobrado con tarjeta. Devuelve el importe por otro medio.',
  MP_NO_CONECTADO: 'Mercado Pago no está conectado en esta sucursal. Conéctalo en Configuración → Mercado Pago y reintenta.',
  MP_CUENTA_REVOCADA: 'La autorización de Mercado Pago fue revocada. Vuelve a conectar la cuenta y reintenta.',
};

/** Traduce un código conocido; null si no hay mensaje propio (se usa el del servidor). */
export function mensajeParaCodigoCaja(codigo: unknown): string | null {
  return typeof codigo === 'string' && Object.hasOwn(MENSAJES_ERROR_CAJA, codigo) ? MENSAJES_ERROR_CAJA[codigo]! : null;
}

/** Muestra el error con el mensaje propio de su código o, si no hay, con el aviso genérico. */
export function avisarErrorCaja(e: unknown): void {
  const mensaje = e instanceof ErrorApi ? mensajeParaCodigoCaja(e.code) : null;
  if (mensaje) toast.error(mensaje);
  else avisarError(e);
}

/** Reintento manual de un reembolso a tarjeta con error (solo `aprobaciones.resolver`). */
export function useReintentarReembolso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reembolsoId: string) => api<ReembolsoTarjetaVista>(`/reembolsos-tarjeta/${reembolsoId}/reintentar`, { method: 'POST' }),
    onSuccess: () => toast.success('Reembolso enviado de nuevo a Mercado Pago'),
    onError: (e) => avisarErrorCaja(e),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['ventas'] }),
  });
}
